/**
 * OrdenesCompra Service
 * CRUD para Ordenes de Compra (GraphQL)
 */

import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { GraphService } from 'src/app/shared/lib/api/graph.service';
import { GraphQLHelper } from 'src/app/shared/lib/helpers/graphql.helper';
import {
    buildGetAllOrdenCompraAvailableQuery,
    buildGetOrdenCompraAvailablePageQuery,
    DELETE_ORDEN_COMPRA,
    DELETE_ORDEN_COMPRA_DE_RC_BY_ID_VINCULO,
    GET_ORDEN_COMPRA,
    VALIDAR_VINCULO_BY_ORDEN_COMPRA_ID,
} from '../graphql/ordenes-compra.queries';

@Injectable({ providedIn: 'root' })
export class OrdenesCompraService {

    constructor(
        private graphService: GraphService,
        private graphQLHelper: GraphQLHelper,
    ) { }


    /**
     * Obtener una Orden de Compra por ID (búsqueda más rápida)
     * Usado en patchFormWithRC para obtener datos frescos al editar RC
     */
    getOCById(id: string): Observable<any> {
        return this.graphService
            .queryGraphQL<{ getOrdenCompra: any }>({
                query: GET_ORDEN_COMPRA,
                variables: { id },
            })
            .pipe(
                map(response => response.getOrdenCompra),
                catchError(error => {
                    console.error(`Error fetching OC ${id}:`, error);
                    return throwError(() => new Error(`Error al obtener orden de compra`));
                }),
            );
    }

    getAllOrdenCompraAvailable(projectionExpression?: string): Observable<any> {
        const selectedFields = projectionExpression
            ? projectionExpression
                .split(',')
                .map((f) => f.trim())
                .filter(Boolean)
            : ['id', 'estado'];

        const fields = this.graphQLHelper.buildGraphQLFields(selectedFields);
        const query = buildGetAllOrdenCompraAvailableQuery(fields);

        return this.graphService.queryGraphQL<any>({
            query,
            variables: { projectionExpression },
        });
    }

    getOrdenCompraAvailablePage(
        projectionExpression?: string,
        limit: number = 100,
        nextToken?: string,
    ): Observable<any> {
        const selectedFields = projectionExpression
            ? projectionExpression.split(',').map((field) => field.trim()).filter(Boolean)
            : ['id', 'estado'];
        const fields = this.graphQLHelper.buildGraphQLFields(selectedFields);

        return this.graphService.queryGraphQL<any>({
            query: buildGetOrdenCompraAvailablePageQuery(fields),
            variables: {
                projectionExpression,
                limit: Math.min(Math.max(limit, 1), 500),
                nextToken: nextToken ?? null,
            },
        });
    }

    deleteOrdenCompra(id: string): Observable<any> {
        return this.graphService.mutateGraphQL<any>({
            mutation: DELETE_ORDEN_COMPRA,
            variables: { id },
        });
    }

    validarVinculoByOrdenCompraId(idVinculado: string): Observable<any> {
        return this.graphService.queryGraphQL<any>({
            query: VALIDAR_VINCULO_BY_ORDEN_COMPRA_ID,
            variables: { idVinculado },
        });
    }

    deleteOrdenCompraDeRegistroCompraByIdVinculoUseCase(
        IdVinculos: string[],
    ): Observable<any> {
        return this.graphService.mutateGraphQL<any>({
            mutation: DELETE_ORDEN_COMPRA_DE_RC_BY_ID_VINCULO,
            variables: { IdVinculos },
        });
    }

    // ========== MÉTODOS ADICIONALES MIGRADOS DE FINANCIAL.SERVICE ==========

    /**
     * @description Crea una nueva orden de compra
     * @param model - Datos de la orden a crear
     * @returns Observable con el resultado de la creación
     */
    create(model: any): Observable<any> {
        return this.graphService.mutateGraphQL<any>({
            mutation: `mutation createOrdenCompra($model: inputOrdenCompra!){
                createOrdenCompra(model: $model){
                    id
                }
            }`,
            variables: { model },
        });
    }

    /**
     * @description Actualiza la fecha de pago de una orden de compra
     * @param id - ID de la orden
     * @param fechaPago - Nueva fecha de pago
     * @returns Observable con el resultado de la actualización
     */
    updateFechaPago(id: string, fechaPago: string): Observable<any> {
        return this.graphService.mutateGraphQL<any>({
            mutation: `mutation updateFechaPagoOC($id: String!,$fechaPago: String!){
                updateFechaPagoOC(id:$id,fechaPago: $fechaPago)
            }`,
            variables: { id, fechaPago },
        });
    }

    /**
     * @description Obtiene todas las órdenes de compra
     * @returns Observable con la lista de órdenes
     */
    getAll(): Observable<any> {
        return this.graphService.queryGraphQL<any>({
            query: `query{
                getAllOrdenCompra{
                    id,
                    createdAt,
                    tipoOC,
                    numeroOC,
                    fechaPago,
                    tipoEgreso,
                    categoriaEgreso,
                    proveedor{
                        id,
                        nombreProveedor,
                        numRUC,
                    },
                    moneda,
                    montoPagar,
                    observaciones,
                    estado,
                    items{
                        numeroItem,
                        descripcion,
                        cantidad,
                        precioUnitario
                    },
                }
            }`,
            variables: {},
        });
    }

    /**
     * @description Obtiene órdenes de compra por fecha de pago
     * @param date - Fecha de pago
     * @returns Observable con la lista de órdenes
     */
    getAllByDate(date: string): Observable<any> {
        return this.graphService.queryGraphQL<any>({
            query: `query getAllOrdenCompraPagosDia($date:String!){
                getAllOrdenCompraPagosDia(date:$date){
                    id,
                    createdAt,
                    tipoOC,
                    numeroOC,
                    fechaPago,
                    proveedor{
                        id,
                        nombreProveedor,
                        numRUC,
                    },
                    tipoEgreso,
                    categoriaEgreso,
                    moneda,
                    montoPagar,
                    observaciones,
                    estado,
                    items{
                        numeroItem,
                        descripcion,
                        cantidad,
                        precioUnitario
                    },
                }
            }`,
            variables: { date },
        });
    }

    /**
     * @description Obtiene órdenes de compra por proveedor
     * @param provider - ID del proveedor
     * @returns Observable con la lista de órdenes
     */
    getAllByProvider(provider: string): Observable<any> {
        return this.graphService.queryGraphQL<any>({
            query: `query getAllOrdenCompraByProvider($provider:String!){
                getAllOrdenCompraByProvider(provider:$provider){
                    id,
                    createdAt,
                    tipoOC,
                    numeroOC,
                    fechaPago,
                    proveedor{
                        id,
                        nombreProveedor,
                        numRUC,
                    },
                    moneda,
                    montoPagar,
                    observaciones,
                    estado,
                    items{
                        numeroItem,
                        descripcion,
                        cantidad,
                        precioUnitario
                    },
                }
            }`,
            variables: { provider },
        });
    }

    /**
     * @description Actualiza una orden de compra completa
     * @param model - Datos de la orden a actualizar
     * @returns Observable con el resultado
     */
    update(model: any): Observable<any> {
        return this.graphService.mutateGraphQL<any>({
            mutation: `mutation updateOC($model: inputOrdenCompra!){
                updateOC(model: $model)
            }`,
            variables: { model },
        });
    }
}
